import type { TaskFilterOptionsDto } from '@sr-sdks/api-sdk-axios';
import { Switch } from '@sr/ui-library';
import { ComponentProps, ReactNode, useState } from 'react';
import {
  FilterCombobox,
  UNNAMED_CLIENT_LABEL,
  withSelectedOptions,
} from '../../../components/FilterCombobox';
import { formatTaskCardDate } from '../../../components/TaskCard';
import {
  TASK_STATUS_OPTIONS,
  type TaskFilterKey,
  UNKNOWN_CLAIMANT_LABEL,
} from './taskFilterOptions';
import type { TaskTabConfig } from './taskTabs';
import type { TaskFilters } from './useTaskFilters';

type TasksFilterBarProps = {
  disabled: boolean;
  /** Undefined while loading, which leaves every dropdown empty. */
  filterOptions?: TaskFilterOptionsDto;
  /** Filter controls other than Status: set when there is nothing to narrow. */
  filtersDisabled: boolean;
  /** Only agent workspaces own clients, so only they get the Client filter. */
  isAgent: boolean;
  search: ReactNode;
  tab: TaskTabConfig;
  taskFilters: TaskFilters;
};

// `FilterCombobox` grows to fill the row by default (`sm:flex-1`). Tabs whose
// design keeps filters compact hold them at a fixed width instead. Below `sm`
// they still grow, or an inline filter (Drafts) shrinks to a truncated label.
const FIXED_FILTER_WIDTH = 'flex-1 sm:w-64 sm:flex-none';

const toDueDateOptions = (days: string[] = []) =>
  days.map((day) => ({ label: formatTaskCardDate(day), value: day }));

/**
 * Search and the tab's filters. Tabs with a toggle collapse their
 * filters behind it; switching it off only hides the controls, so selections
 * stay applied and stay in the URL.
 */
export const TasksFilterBar = ({
  disabled,
  filterOptions,
  filtersDisabled,
  isAgent,
  search,
  tab,
  taskFilters,
}: TasksFilterBarProps) => {
  const {
    client,
    dueDate,
    isResolvedOnly,
    otherClaimant,
    otherClaimantOptions,
    status,
  } = taskFilters;

  const [isFiltersOpen, setIsFiltersOpen] = useState(true);

  // Resolved cards show no due date to filter on (APP-2631).
  const visibleFilters = tab.filters.filter(
    (filter) =>
      (filter !== 'client' || isAgent) &&
      (filter !== 'dueDate' || !isResolvedOnly),
  );

  const filterWidth = tab.isFilterRowFullWidth ? '' : FIXED_FILTER_WIDTH;

  const filterConfigs: Record<
    TaskFilterKey,
    Omit<ComponentProps<typeof FilterCombobox>, 'className' | 'disabled'>
  > = {
    client: {
      'aria-label': 'Client',
      filter: client,
      hasSearch: true,
      options: withSelectedOptions(
        filterOptions?.clients ?? [],
        client.selected,
        () => UNNAMED_CLIENT_LABEL,
      ),
      placeholder: 'Client',
      selectedPlaceholder: 'Clients',
      // `useCombobox` sorts by `value`, a uuid here; the API sends them sorted by name.
      sorting: 'none',
    },
    dueDate: {
      'aria-label': 'Due date',
      filter: dueDate,
      options: withSelectedOptions(
        toDueDateOptions(filterOptions?.dueDates),
        dueDate.selected,
        formatTaskCardDate,
      ),
      placeholder: 'Due date',
      selectedPlaceholder: 'Due dates',
      // Values are YYYY-MM-DD, so sorting by value is chronological, and
      // it also slots a kept stale selection into place.
      sorting: 'asc',
    },
    otherClaimant: {
      'aria-label': 'Other claimant',
      filter: otherClaimant,
      hasSearch: true,
      options: withSelectedOptions(
        otherClaimantOptions,
        otherClaimant.selected,
        () => UNKNOWN_CLAIMANT_LABEL,
      ),
      placeholder: 'Other claimant',
      selectedPlaceholder: 'Other claimants',
      // `useCombobox` sorts by `value`, uuids here; the API sends them sorted by name.
      sorting: 'none',
    },
    status: {
      'aria-label': 'Status',
      filter: status,
      options: TASK_STATUS_OPTIONS,
      placeholder: 'Status',
      selectedPlaceholder: 'Statuses',
      // `TASK_STATUS_OPTIONS` is already sorted A→Z; keep that order as given.
      sorting: 'none',
    },
  };

  const controls = visibleFilters.map((filter) => (
    <FilterCombobox
      key={filter}
      className={filterWidth}
      // Status stays usable on an empty list: Resolved reaches completed claims
      // the open list leaves out (APP-2577).
      disabled={filtersDisabled && filter !== 'status'}
      {...filterConfigs[filter]}
    />
  ));

  const hasToggle = tab.hasFiltersToggle && visibleFilters.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {search}

        {hasToggle && (
          <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
            <Switch
              aria-label="Filters"
              checked={isFiltersOpen}
              disabled={disabled}
              onCheckedChange={setIsFiltersOpen}
            />
            Filters
          </label>
        )}

        {!tab.hasFiltersToggle && controls}
      </div>

      {hasToggle && isFiltersOpen && (
        // One full-width filter per row on phones, a single wrapping row from `sm` up.
        <div className="grid grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:items-center">
          {controls}
        </div>
      )}
    </div>
  );
};
